// frontend/src/components/responses/CreateResponseModal.tsx - UPDATED FOR HOOKS/STORE INTEGRATION
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { cn, formatDate } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';

// FIXED: Use entity-specific hooks
import { useWorkflows } from '@/hooks/useWorkflows';
import { useCreateResponse } from '@/hooks/useResponses';
import { useUploadAttachments } from '@/hooks/useAttachments';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FileUpload } from '@/components/ui/FileUpload';

// FIXED: Import API types
import type { 
  Workflow, 
  Correspondence, 
  ResponseCreateRequest,
  NormalizedUser,
  Priority,
  WorkflowQueryParams
} from '@/api/types';

// ===== ERROR BOUNDARY =====

class CreateResponseErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('CreateResponseModal Error Boundary:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('CreateResponseModal Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                Modal Error
              </h3>
              <p className="text-red-700 dark:text-red-300 text-sm">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              <Button 
                onClick={() => this.setState({ hasError: false })} 
                size="sm" 
                variant="secondary"
                className="mt-3"
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ===== INTERFACE =====

interface CreateResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  correspondence?: Correspondence; // Optional - for manual response creation
  onSuccess?: (response: any) => void;
}

// ===== MAIN COMPONENT =====

export const CreateResponseModal: React.FC<CreateResponseModalProps> = ({
  isOpen,
  onClose,
  correspondence,
  onSuccess
}) => {
  // Store integration
  const { user: currentUser } = useAuthStore();
  const { 
    globalError, 
    setGlobalError, 
    clearGlobalError,
    setApiLoading,
    clearApiLoading
  } = useAppStore();

  // Form state
  const [mailSubject, setMailSubject] = useState('');
  const [mailBody, setMailBody] = useState('');
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
  const [hasManuallySelectedWorkflow, setHasManuallySelectedWorkflow] = useState(false);
  const [priority, setPriority] = useState<Priority>('normal');
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [selectedVisibilityGroups, setSelectedVisibilityGroups] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedAttachmentIds, setUploadedAttachmentIds] = useState<string[]>([]);

  // Get organization ID with proper type safety
  const organizationId = useMemo(() => {
    if (!currentUser?.organization) {
      return null;
    }
    
    // Handle both string and object formats from NormalizedUser
    if (typeof currentUser.organization === 'string') {
      return currentUser.organization;
    } else if (currentUser.organization && typeof currentUser.organization === 'object') {
      return (currentUser.organization as any)._id || (currentUser.organization as any).id || null;
    }
    
    return null;
  }, [currentUser?.organization]);

  // Workflows query params
  const workflowsQueryParams = useMemo((): WorkflowQueryParams | undefined => {
    if (!organizationId) return undefined;

    return {
      // Don't filter by isActive - show all workflows (active and inactive)
      // Users should be able to use any workflow they have access to
      sortBy: 'name',
      sortOrder: 'asc',
      limit: 50
      // Don't pass organizationId - backend uses req.user.organization for non-implementors
      // Passing it explicitly triggers a security check that rejects it for contributors
    };
  }, [organizationId]);

  // API hooks
  const { 
    data: workflowsData, 
    loading: workflowsLoading, 
    error: workflowsError 
  } = useWorkflows(workflowsQueryParams);

  // Create response mutation
  const createResponseMutation = useCreateResponse({
    onSuccess: (response) => {
      clearGlobalError();
      onSuccess?.(response);
      onClose();
    },
    onError: (error: any) => {
      console.error('Failed to create response:', error);
      setGlobalError(error.message || 'Failed to create response');
    }
  });

  // Upload attachments mutation
  const uploadAttachmentsMutation = useUploadAttachments({
    onSuccess: (attachments) => {
      const attachmentIds = attachments.map(att => (att as any)._id || (att as any).id);
      setUploadedAttachmentIds(attachmentIds);
    },
    onError: (error: any) => {
      console.error('Failed to upload attachments:', error);
      setGlobalError(error.message || 'Failed to upload attachments');
    }
  });

  // Extract available workflows
  const availableWorkflows = useMemo(() => {
    if (!organizationId || workflowsLoading || workflowsError) {
      return [];
    }

    let workflows: Workflow[] = [];

    if (workflowsData?.data && Array.isArray(workflowsData.data)) {
      // Filter to only show active workflows from user's organization
      const allWorkflows = workflowsData.data;
      workflows = allWorkflows.filter((w: Workflow) => {
        if (!w || !w.isActive) return false;

        // Get workflow's organization ID
        const workflowOrgId = typeof w.organization === 'string'
          ? w.organization
          : (w.organization as any)?._id || (w.organization as any)?.id;

        // Only include workflows from user's organization
        const matchesOrg = workflowOrgId && workflowOrgId === organizationId;

        if (!matchesOrg) {
          console.warn('CreateResponseModal: Filtering out workflow from different organization', {
            workflowId: w._id || w.id,
            workflowName: w.name,
            workflowOrg: workflowOrgId,
            userOrg: organizationId
          });
        }

        return matchesOrg;
      });
    } else if (Array.isArray(workflowsData)) {
      const allWorkflows = workflowsData as Workflow[];
      workflows = allWorkflows.filter((w: Workflow) => {
        if (!w || !w.isActive) return false;

        // Get workflow's organization ID
        const workflowOrgId = typeof w.organization === 'string'
          ? w.organization
          : (w.organization as any)?._id || (w.organization as any)?.id;

        // Only include workflows from user's organization
        return workflowOrgId && workflowOrgId === organizationId;
      });
    } else {
    }

    return workflows;
  }, [workflowsData, workflowsLoading, workflowsError, organizationId]);

  // DISABLED: Don't auto-select workflow due to backend lookup issues
  // Users should manually select a workflow or proceed without one
  // useEffect(() => {
  //   if (availableWorkflows.length > 0 && !selectedWorkflowId && !workflowsLoading && isOpen && !hasManuallySelectedWorkflow) {
  //     const firstWorkflow = availableWorkflows[0];
  //     const workflowId = firstWorkflow._id || firstWorkflow.id || '';
  //     setSelectedWorkflowId(workflowId);
  //   }
  // }, [availableWorkflows, workflowsLoading, isOpen, selectedWorkflowId, hasManuallySelectedWorkflow]);

  // Initialize form with correspondence data
  useEffect(() => {
    if (isOpen && correspondence) {
      // Generate default subject
      const originalSubject = correspondence.subject || 'No Subject';
      const replySubject = originalSubject.startsWith('Re:') 
        ? originalSubject 
        : `Re: ${originalSubject}`;
      
      setMailSubject(replySubject);
      
      // Generate default body template
      const dateStr = formatDate(correspondence.createdAt);
      
      const defaultBody = `


---
Original Message:
Date: ${dateStr}
Subject: ${originalSubject}

${correspondence.body ? `${correspondence.body.substring(0, 200)}...` : '[Content preview not available]'}`;
      
      setMailBody(defaultBody);
    }
  }, [isOpen, correspondence]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMailSubject('');
      setMailBody('');
      setSelectedWorkflowId('');
      setHasManuallySelectedWorkflow(false);
      setPriority('normal');
      setSelectedFiles([]);
      setErrors({});
      clearGlobalError();
    }
  }, [isOpen, clearGlobalError]);

  // Loading state management
  useEffect(() => {
    if (workflowsLoading || createResponseMutation.loading || uploadAttachmentsMutation.loading) {
      setApiLoading('responses', true);
    } else {
      setApiLoading('responses', false);
    }

    return () => clearApiLoading();
  }, [workflowsLoading, createResponseMutation.loading, uploadAttachmentsMutation.loading, setApiLoading, clearApiLoading]);

  // Event handlers
  const handleClose = useCallback(() => {
    if (!createResponseMutation.loading) {
      onClose();
    }
  }, [createResponseMutation.loading, onClose]);

  const handleWorkflowChange = useCallback((workflowId: string) => {
    setSelectedWorkflowId(workflowId);
    setHasManuallySelectedWorkflow(true);
  }, []);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};
    
    if (!mailSubject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!mailBody.trim()) {
      newErrors.body = 'Message body is required';
    }
    
    if (!organizationId) {
      newErrors.organization = 'Organization context is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [mailSubject, mailBody, organizationId]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    clearGlobalError();

    try {
      // Note: Attachments will be added AFTER response creation
      // The backend requires a parent entity (responseId) for attachments
      // So we'll create the response first, then attachments can be added separately

      // Step 1: Build source IDs array (empty if no correspondence provided)
      const sourceIds: string[] = [];
      if (correspondence) {
        const correspondenceId = correspondence._id || correspondence.id || '';
        if (!correspondenceId) {
          setGlobalError('Invalid correspondence: missing ID');
          return;
        }
        sourceIds.push(correspondenceId);
      }

      // Step 2: Build request data (attachments will be added after response creation)
      const requestData: any = {
        sourceIds,
        mailSubject: mailSubject.trim(),
        mailBody: mailBody.trim(),
        priority
      };

      // Only include workflowId if one is selected
      if (selectedWorkflowId) {
        requestData.workflowId = selectedWorkflowId;
      }

      // ✅ FIXED: Inherit project from correspondence if available
      if (correspondence) {
        // If correspondence has a project, inherit it (handle both string and populated object)
        if (correspondence.projectId) {
          const projId = typeof correspondence.projectId === 'object'
            ? (correspondence.projectId as any)._id || (correspondence.projectId as any).id
            : correspondence.projectId;
          if (projId) {
            requestData.projectIds = [projId];
          }
        }
        // If correspondence has visibility groups, inherit them (handle populated objects)
        if ((correspondence as any).visibilityGroups && Array.isArray((correspondence as any).visibilityGroups)) {
          requestData.visibilityGroups = (correspondence as any).visibilityGroups.map((g: any) =>
            typeof g === 'object' ? (g._id || g.id) : g
          ).filter(Boolean);
        }
      } else {
        // Only use manual selections if no correspondence (manual response creation)
        if (selectedProjectIds.length > 0) {
          requestData.projectIds = selectedProjectIds;
        }
        if (selectedVisibilityGroups.length > 0) {
          requestData.visibilityGroups = selectedVisibilityGroups;
        }
      }

      // Step 3: Create response (without attachments for now)
      const createdResponse = await createResponseMutation.mutate(requestData);

      // Upload attachments after response is created
      if (selectedFiles.length > 0 && createdResponse) {
        // The create endpoint returns { message, response: { _id, ... } } — extract the ID
        const responseId = createdResponse._id || (createdResponse as any).response?._id || createdResponse.id || (createdResponse as any).response?.id;
        if (responseId) {
          try {
            await uploadAttachmentsMutation.mutate({
              files: selectedFiles,
              responseId,
            });
          } catch (uploadError: any) {
            console.error('Attachment upload failed:', uploadError);
            // Response was created successfully, just attachment upload failed
            setGlobalError('Response created but attachment upload failed. You can add attachments from the response editor.');
          }
        }
      }
    } catch (error) {
      console.error('Response creation error:', error);
    }
  }, [validateForm, clearGlobalError, correspondence, mailSubject, mailBody, priority, selectedWorkflowId, selectedProjectIds, selectedVisibilityGroups, selectedFiles, setGlobalError, createResponseMutation]);

  if (!isOpen) return null;

  // Show organization context error
  if (!organizationId) {
    return (
      <CreateResponseErrorBoundary>
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Organization Required
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Unable to create response: No organization context available. 
              Please ensure you are logged in and assigned to an organization.
            </p>
            <div className="flex justify-end">
              <Button onClick={handleClose}>Close</Button>
            </div>
          </div>
        </div>
      </CreateResponseErrorBoundary>
    );
  }

  return (
    <CreateResponseErrorBoundary>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black bg-opacity-50" 
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Create Response
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {correspondence
                  ? `Responding to: ${correspondence.subject || 'Unknown Subject'}`
                  : 'Creating new manual response'}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={createResponseMutation.loading}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Global Error Display */}
            {globalError && (
              <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{globalError}</p>
              </div>
            )}

            {/* Source Correspondence Info */}
            {correspondence && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Source Correspondence
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <div><strong>Subject:</strong> {correspondence.subject || 'No Subject'}</div>
                  <div><strong>Date:</strong> {formatDate(correspondence.createdAt)}</div>
                  <div><strong>Type:</strong> {correspondence.manual ? 'Manual' : 'Classified'}</div>
                  {correspondence.projectId && (
                    <div><strong>Project:</strong> Associated with project</div>
                  )}
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Workflow Selection */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Workflow (Optional)
                  </label>
                  <select
                    value={selectedWorkflowId}
                    onChange={(e) => handleWorkflowChange(e.target.value)}
                    disabled={createResponseMutation.loading || workflowsLoading}
                    className={cn(
                      "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                      "border-gray-300 dark:border-gray-600"
                    )}
                  >
                    <option value="">
                      {workflowsLoading 
                        ? 'Loading workflows...' 
                        : availableWorkflows.length === 0 
                        ? 'No workflows available'
                        : 'No workflow (manual response)'
                      }
                    </option>
                    {availableWorkflows.map((workflow) => {
                      const workflowId = workflow._id || workflow.id || '';
                      return (
                        <option key={workflowId} value={workflowId}>
                          {workflow.name} ({workflow.stages?.length || 0} stages)
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {workflowsLoading
                      ? 'Loading available workflows...'
                      : availableWorkflows.length > 0
                      ? 'Note: If workflow selection causes errors, choose "No workflow (manual response)" to proceed without a workflow.'
                      : 'Response will be created as a manual response without workflow processing'
                    }
                  </p>
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Priority
                  </label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as Priority)}
                    disabled={createResponseMutation.loading}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject *
                </label>
                <Input
                  type="text"
                  value={mailSubject}
                  onChange={(e) => setMailSubject(e.target.value)}
                  placeholder="Enter response subject"
                  disabled={createResponseMutation.loading}
                  className={cn(
                    "w-full",
                    errors.subject && "border-red-300 dark:border-red-600"
                  )}
                />
                {errors.subject && (
                  <p className="text-red-600 dark:text-red-400 text-xs mt-1">{errors.subject}</p>
                )}
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Message Body *
                </label>
                <textarea
                  value={mailBody}
                  onChange={(e) => setMailBody(e.target.value)}
                  placeholder="Enter your response message"
                  disabled={createResponseMutation.loading}
                  rows={12}
                  className={cn(
                    "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-vertical",
                    errors.body ? "border-red-300 dark:border-red-600" : "border-gray-300 dark:border-gray-600"
                  )}
                />
                {errors.body && (
                  <p className="text-red-600 dark:text-red-400 text-xs mt-1">{errors.body}</p>
                )}
              </div>

              {/* Attachments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Attachments (Optional)
                </label>
                <FileUpload
                  files={selectedFiles}
                  onChange={setSelectedFiles}
                  onRemove={(index) => {
                    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
                  }}
                  maxFiles={20}
                  maxFileSize={100 * 1024 * 1024} // 100MB
                  disabled={createResponseMutation.loading || uploadAttachmentsMutation.loading}
                  error={errors.attachments}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  You can attach documents, images, and other files to your response. Maximum 20 files, 100MB each.
                </p>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleClose}
                  disabled={createResponseMutation.loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createResponseMutation.loading || uploadAttachmentsMutation.loading || workflowsLoading}
                >
                  {uploadAttachmentsMutation.loading ? (
                    <div className="flex items-center space-x-2">
                      <LoadingSpinner size="sm" />
                      <span>Uploading attachments...</span>
                    </div>
                  ) : createResponseMutation.loading ? (
                    <div className="flex items-center space-x-2">
                      <LoadingSpinner size="sm" />
                      <span>Creating response...</span>
                    </div>
                  ) : (
                    'Create Response'
                  )}
                </Button>
              </div>
            </form>

            {/* Workflows Loading/Error State */}
            {workflowsLoading && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-6">
                <div className="flex items-center space-x-3">
                  <LoadingSpinner size="sm" />
                  <span className="text-sm text-blue-700 dark:text-blue-300">
                    Loading available workflows...
                  </span>
                </div>
              </div>
            )}

            {workflowsError && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mt-6">
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      <strong>Workflows Unavailable:</strong> {workflowsError}
                    </p>
                    <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                      You can still create a manual response without workflow processing.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </CreateResponseErrorBoundary>
  );
};